import React, { useState, useEffect, useRef } from 'react';
import { User, Mailbox } from '../types';
import api, { TelegramWhitelistItem } from '../services/api';
import { formatDateMsk, formatDateTimeMsk } from '../utils/dateTime';
import './AdminPanel.css';
import { mailboxAddress } from '../utils/mailboxAddress';

interface AdminPanelProps {
  onBack: () => void;
  onShowNotification?: (message: string, type: 'error' | 'success') => void;
}

interface PendingUser extends User {
  note: string;
  plainPassword: string;
  isApproved: boolean;
  children?: Array<{ id: number; username: string; telegramId?: string | null; color?: string | null }>;
}

interface AdminMailbox extends Mailbox {
  plainPassword?: string;
  account?: string;
  owner?: {
    id: number;
    username: string;
    telegramId?: string | null;
    telegramProfile?: {
      username?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      photoUrl?: string | null;
    } | null;
  };
}

type AdminTab = 'users' | 'mailboxes' | 'telegram';

const AdminPanel: React.FC<AdminPanelProps> = ({ onBack, onShowNotification }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('users');
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [mailboxes, setMailboxes] = useState<AdminMailbox[]>([]);
  const [telegramItems, setTelegramItems] = useState<TelegramWhitelistItem[]>([]);
  const [telegramAddValue, setTelegramAddValue] = useState('');
  const [telegramBusyId, setTelegramBusyId] = useState<string | null>(null);
  const [brokenTelegramAvatars, setBrokenTelegramAvatars] = useState<Record<string, true>>({});
  const [brokenOwnerAvatars, setBrokenOwnerAvatars] = useState<Record<string, true>>({});

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferFromId, setTransferFromId] = useState('');
  const [transferToId, setTransferToId] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTargetId, setShareTargetId] = useState('');
  const [shareSourceId, setShareSourceId] = useState('');
  const [shareBusy, setShareBusy] = useState(false);

  const telegramAutoRefreshPendingRef = useRef<Set<string>>(new Set());
  const telegramAutoRefreshAttemptedRef = useRef<Set<string>>(new Set());
  const telegramAutoRefreshInFlightRef = useRef(false);
  const telegramAutoRefreshTimerRef = useRef<number | null>(null);

  // Users-tab: king/child management (by userId)
  const [childrenOpen, setChildrenOpen] = useState(false);
  const [childrenKingUserId, setChildrenKingUserId] = useState<number | null>(null);
  const [childrenList, setChildrenList] = useState<Array<{
    id: number;
    username: string;
    telegramId?: string | null;
    color?: string | null;
  }>>([]);
  const [childrenBusy, setChildrenBusy] = useState(false);
  const [childrenAddValue, setChildrenAddValue] = useState('');

  // Telegram-tab: children per kingTelegramId
  const [telegramChildrenByKing, setTelegramChildrenByKing] = useState<Record<string, any[]>>({});
  const [telegramChildrenOpenByKing, setTelegramChildrenOpenByKing] = useState<Record<string, true>>({});
  const [telegramChildAddByKing, setTelegramChildAddByKing] = useState<Record<string, string>>({});
  
  // Поиск
  const [userSearch, setUserSearch] = useState('');
  const [mailboxSearch, setMailboxSearch] = useState('');
  const [mailboxPage, setMailboxPage] = useState(1);
  const [mailboxHasMore, setMailboxHasMore] = useState(false);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxSortBy, setMailboxSortBy] = useState<'created_at' | 'owner_username' | 'owner_telegram_id'>('created_at');
  const [mailboxSortDir, setMailboxSortDir] = useState<'asc' | 'desc'>('desc');
  const [mailboxSortOpen, setMailboxSortOpen] = useState(false);
  const [mailboxOwners, setMailboxOwners] = useState<Array<{
    id: number;
    username: string;
    telegramId?: string | null;
    mailboxCount: number;
    telegramProfile?: {
      username?: string | null;
      firstName?: string | null;
      lastName?: string | null;
      photoUrl?: string | null;
    } | null;
  }>>([]);
  const [mailboxOwnerIds, setMailboxOwnerIds] = useState<number[]>([]);
  const [mailboxOwnersOpen, setMailboxOwnersOpen] = useState(false);
  const mailboxSearchDebounceRef = useRef<number | null>(null);
  const mailboxSortInitRef = useRef(false);
  const mailboxSortBtnRef = useRef<HTMLButtonElement | null>(null);
  const mailboxOwnersBtnRef = useRef<HTMLButtonElement | null>(null);
  const mailboxSortMenuRef = useRef<HTMLDivElement | null>(null);
  const mailboxOwnersMenuRef = useRef<HTMLDivElement | null>(null);
  const [mailboxSortMenuPos, setMailboxSortMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  const [mailboxOwnersMenuPos, setMailboxOwnersMenuPos] = useState<{ top: number; left: number; minWidth: number } | null>(null);
  
  // Состояния для модальных окон
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    // Принудительно загружаем токен из localStorage
    api.refreshToken();
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      // Проверяем токен
      let token = api.getToken();
      
      // Если токена нет, показываем ошибку
      if (!token) {
        onShowNotification?.('Необходимо войти в систему для доступа к админке', 'error');
        return;
      }
      
      // Загружаем данные пользователей из API
      const usersResponse = await api.getAdminUsers();
      
      if (usersResponse.success && usersResponse.data) {
        // Преобразуем данные API в формат компонента
        const realUsers: PendingUser[] = (usersResponse.data as any).users.map((user: any) => ({
          id: user.id,
          username: user.username,
          isAdmin: user.isAdmin,
          telegramId: user.telegramId ?? null,
          parentUserId: user.parentUserId ?? null,
          note: user.note || '',
          plainPassword: user.plainPassword || 'не задан',
          isApproved: user.isApproved,
          email: user.email,
          children: user.children || []
        }));
        setUsers(realUsers);
      } else {
        onShowNotification?.('Ошибка загрузки пользователей: ' + (usersResponse.message || 'Неизвестная ошибка'), 'error');
      }

      // Загружаем данные ящиков из API (необязательно)
      try {
        setMailboxLoading(true);
        const mailboxesResponse = await api.getAdminMailboxes('', 1, 50, mailboxSortBy, mailboxSortDir, mailboxOwnerIds);
        
        if (mailboxesResponse.success && mailboxesResponse.data) {
          if ((mailboxesResponse.data as any).mailboxes) {
            const payload: any = mailboxesResponse.data;
            const realMailboxes: AdminMailbox[] = payload.mailboxes.map((mailbox: any) => ({
              id: mailbox.id,
              name: mailbox.name,
              account: mailbox.account,
              hasPassword: mailbox.hasPassword,
              emailCount: mailbox.emailCount,
              createdAt: mailbox.createdAt,
              lastChecked: mailbox.lastChecked,
              plainPassword: mailbox.plainPassword || 'не задан',
              owner: mailbox.owner
            }));
            setMailboxes(realMailboxes);
            const pg = payload.pagination;
            setMailboxPage(pg?.page || 1);
            setMailboxHasMore(Boolean(pg && pg.page < pg.totalPages));
          } else {
            setMailboxes([]);
            setMailboxPage(1);
            setMailboxHasMore(false);
          }
        } else {
          setMailboxes([]);
          setMailboxPage(1);
          setMailboxHasMore(false);
        }
      } catch (mailboxError) {
        setMailboxes([]);
        setMailboxPage(1);
        setMailboxHasMore(false);
      } finally {
        setMailboxLoading(false);
      }

      // Загружаем белый список Telegram (необязательно)
      try {
        const telegramResponse = await api.getTelegramWhitelistItems();
        if (telegramResponse.success && telegramResponse.data) {
          setTelegramItems(telegramResponse.data.whitelist || []);
        } else {
          setTelegramItems([]);
        }
      } catch (telegramError) {
        setTelegramItems([]);
      }

      // Загружаем список владельцев (для фильтра ящиков)
      try {
        const ownersResp = await api.getAdminMailboxOwners();
        if (ownersResp.success && ownersResp.data) {
          setMailboxOwners(ownersResp.data.owners || []);
        } else {
          setMailboxOwners([]);
        }
      } catch {
        setMailboxOwners([]);
      }
      
    } catch (error) {
      onShowNotification?.('Ошибка загрузки данных админки: ' + (error as Error).message, 'error');
    }
  };

  useEffect(() => {
    // Avoid double-load on initial mount (loadData already fetches).
    if (!mailboxSortInitRef.current) {
      mailboxSortInitRef.current = true;
      return;
    }
    void reloadMailboxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailboxSortBy, mailboxSortDir, mailboxOwnerIds]);

  useEffect(() => {
    const updateSortPos = () => {
      if (!mailboxSortOpen) return;
      const btn = mailboxSortBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const minWidth = Math.max(320, Math.round(r.width));
      const maxLeft = Math.max(8, window.innerWidth - minWidth - 8);
      setMailboxSortMenuPos({
        top: r.bottom + 8,
        left: Math.min(Math.max(8, r.left), maxLeft),
        minWidth
      });
    };

    const updateOwnersPos = () => {
      if (!mailboxOwnersOpen) return;
      const btn = mailboxOwnersBtnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const minWidth = Math.max(360, Math.round(r.width));
      const maxLeft = Math.max(8, window.innerWidth - minWidth - 8);
      setMailboxOwnersMenuPos({
        top: r.bottom + 8,
        left: Math.min(Math.max(8, r.left), maxLeft),
        minWidth
      });
    };

    updateSortPos();
    updateOwnersPos();

    const onScrollCapture = () => {
      updateSortPos();
      updateOwnersPos();
    };
    const onResize = () => {
      updateSortPos();
      updateOwnersPos();
    };

    window.addEventListener('resize', onResize);
    // capture scroll from any container
    document.addEventListener('scroll', onScrollCapture, true);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('scroll', onScrollCapture, true);
    };
  }, [mailboxSortOpen, mailboxOwnersOpen]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;

      if (mailboxSortOpen) {
        if (mailboxSortBtnRef.current?.contains(target)) return;
        if (mailboxSortMenuRef.current?.contains(target)) return;
        setMailboxSortOpen(false);
      }
      if (mailboxOwnersOpen) {
        if (mailboxOwnersBtnRef.current?.contains(target)) return;
        if (mailboxOwnersMenuRef.current?.contains(target)) return;
        setMailboxOwnersOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [mailboxSortOpen, mailboxOwnersOpen]);

  const loadMoreMailboxes = async () => {
    if (mailboxLoading || !mailboxHasMore) return;
    try {
      setMailboxLoading(true);
      const nextPage = mailboxPage + 1;
      const resp = await api.getAdminMailboxes(mailboxSearch, nextPage, 50, mailboxSortBy, mailboxSortDir, mailboxOwnerIds);
      if (resp.success && resp.data) {
        const payload: any = resp.data;
        const newItems: AdminMailbox[] = (payload.mailboxes || []).map((mailbox: any) => ({
          id: mailbox.id,
          name: mailbox.name,
          account: mailbox.account,
          hasPassword: mailbox.hasPassword,
          emailCount: mailbox.emailCount,
          createdAt: mailbox.createdAt,
          lastChecked: mailbox.lastChecked,
          plainPassword: mailbox.plainPassword || 'не задан',
          owner: mailbox.owner
        }));
        setMailboxes(prev => [...prev, ...newItems]);
        setMailboxPage(payload.pagination?.page || nextPage);
        setMailboxHasMore(Boolean(payload.pagination && payload.pagination.page < payload.pagination.totalPages));
      }
    } catch {
      // ignore
    } finally {
      setMailboxLoading(false);
    }
  };

  const reloadMailboxes = async (opts?: { resetPage?: boolean }) => {
    const resetPage = opts?.resetPage !== false;
    try {
      setMailboxLoading(true);
      const resp = await api.getAdminMailboxes(mailboxSearch, 1, 50, mailboxSortBy, mailboxSortDir, mailboxOwnerIds);
      if (resp.success && resp.data) {
        const payload: any = resp.data;
        const items: AdminMailbox[] = (payload.mailboxes || []).map((mailbox: any) => ({
          id: mailbox.id,
          name: mailbox.name,
          account: mailbox.account,
          hasPassword: mailbox.hasPassword,
          emailCount: mailbox.emailCount,
          createdAt: mailbox.createdAt,
          lastChecked: mailbox.lastChecked,
          plainPassword: mailbox.plainPassword || 'не задан',
          owner: mailbox.owner
        }));
        setMailboxes(items);
        if (resetPage) {
          setMailboxPage(payload.pagination?.page || 1);
        }
        setMailboxHasMore(Boolean(payload.pagination && payload.pagination.page < payload.pagination.totalPages));
      }
    } finally {
      setMailboxLoading(false);
    }
  };

  const handleApproveUser = async (userId: number) => {
    try {
      const response = await api.approveUser(userId, true);
      if (response.success) {
        // Обновляем локальное состояние после успешного API запроса
        setUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, isApproved: true } : user
        ));
        onShowNotification?.('Пользователь одобрен', 'success');
      } else {
        onShowNotification?.('Ошибка одобрения: ' + response.message, 'error');
      }
    } catch (error) {
      onShowNotification?.('Ошибка одобрения пользователя', 'error');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    const user = users.find(u => u.id === userId);
    if (user && window.confirm(`Удалить пользователя "${user.username}"?`)) {
      try {
        const response = await api.deleteUser(userId);
        if (response.success) {
          // Обновляем локальное состояние после успешного API запроса
          setUsers(prev => prev.filter(user => user.id !== userId));
          onShowNotification?.(`Пользователь ${user.username} удален`, 'success');
        } else {
          onShowNotification?.('Ошибка удаления: ' + response.message, 'error');
        }
      } catch (error) {
        onShowNotification?.('Ошибка удаления пользователя', 'error');
      }
    }
  };

  const handleChangePassword = async (userId: number) => {
    if (newPassword.trim()) {
      try {
        const response = await api.changeUserPassword(userId, newPassword);
        if (response.success) {
          // Обновляем локальное состояние после успешного API запроса
          setUsers(prev => prev.map(user => 
            user.id === userId ? { ...user, plainPassword: 'скрыт' } : user
          ));
          onShowNotification?.('Пароль изменен', 'success');
          setEditingUserId(null);
          setNewPassword('');
        } else {
          onShowNotification?.('Ошибка смены пароля: ' + response.message, 'error');
        }
      } catch (error) {
        onShowNotification?.('Ошибка смены пароля', 'error');
      }
    }
  };

  const normalizeTelegramId = (value: string) => {
    const s = String(value ?? '').trim();
    if (!/^\d+$/.test(s)) return null;
    return s;
  };

  const reloadTelegramItems = async () => {
    try {
      const resp = await api.getTelegramWhitelistItems();
      if (resp.success && resp.data) {
        const next = resp.data.whitelist || [];
        setTelegramItems(next);
        // Auto-refresh profiles (no manual clicks): try once per telegramId per session.
        for (const it of next as any[]) {
          const id = it?.telegramId ? String(it.telegramId) : null;
          if (id && it?.isActive && (!it?.displayName || !it?.photoUrl)) {
            scheduleAutoRefreshTelegramProfile(id);
          }
          const kids = Array.isArray(it?.children) ? it.children : [];
          for (const kid of kids) {
            const kidId = kid?.telegramId ? String(kid.telegramId) : null;
            if (kidId && (!kid?.displayName || !kid?.photoUrl)) {
              scheduleAutoRefreshTelegramProfile(kidId);
            }
          }
        }
        // Allow retry after we potentially refreshed profile URLs.
        setBrokenTelegramAvatars((prev) => {
          const keys = Object.keys(prev);
          if (keys.length === 0) return prev;
          const shouldClear = new Set<string>();
          for (const it of next as any[]) {
            if (it?.telegramId && it?.photoUrl) shouldClear.add(String(it.telegramId));
            const kids = Array.isArray(it?.children) ? it.children : [];
            for (const kid of kids) {
              if (kid?.telegramId && kid?.photoUrl) shouldClear.add(String(kid.telegramId));
            }
          }
          if (shouldClear.size === 0) return prev;
          const out: Record<string, true> = {};
          for (const k of keys) {
            if (!shouldClear.has(k)) out[k] = true;
          }
          return out;
        });
      }
    } catch {
      // ignore - tab can still render
    }
  };

  const runAutoRefreshTelegramProfiles = async () => {
    if (telegramAutoRefreshInFlightRef.current) return;
    telegramAutoRefreshInFlightRef.current = true;
    try {
      const ids = Array.from(telegramAutoRefreshPendingRef.current);
      telegramAutoRefreshPendingRef.current.clear();
      if (ids.length === 0) return;

      for (const id of ids) {
        if (!id) continue;
        if (telegramAutoRefreshAttemptedRef.current.has(id)) continue;
        telegramAutoRefreshAttemptedRef.current.add(id);
        try {
          // Best-effort: do not show notifications and do not block UI.
          await api.refreshTelegramWhitelistItem(id);
        } catch {
          // ignore
        }
      }

      await reloadTelegramItems();
    } finally {
      telegramAutoRefreshInFlightRef.current = false;
    }
  };

  const scheduleAutoRefreshTelegramProfile = (telegramId: string, force = false) => {
    if (!telegramId) return;
    if (force) {
      telegramAutoRefreshAttemptedRef.current.delete(telegramId);
    }
    if (telegramAutoRefreshAttemptedRef.current.has(telegramId)) return;
    telegramAutoRefreshPendingRef.current.add(telegramId);
    if (telegramAutoRefreshTimerRef.current) {
      window.clearTimeout(telegramAutoRefreshTimerRef.current);
    }
    telegramAutoRefreshTimerRef.current = window.setTimeout(() => {
      telegramAutoRefreshTimerRef.current = null;
      void runAutoRefreshTelegramProfiles();
    }, 250);
  };

  const handleTelegramAvatarError = (telegramId: string) => {
    setBrokenTelegramAvatars((prev) => (prev[telegramId] ? prev : { ...prev, [telegramId]: true }));
    scheduleAutoRefreshTelegramProfile(telegramId, true);
  };

  const handleAddTelegramId = async () => {
    const telegramId = normalizeTelegramId(telegramAddValue);
    if (!telegramId) {
      onShowNotification?.('Telegram ID должен содержать только цифры', 'error');
      return;
    }
    try {
      setTelegramBusyId(telegramId);
      const resp = await api.addTelegramWhitelistItem(telegramId);
      if (resp.success) {
        setTelegramAddValue('');
        onShowNotification?.('Telegram ID добавлен', 'success');
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка добавления: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка добавления Telegram ID: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleDeleteTelegramId = async (telegramId: string) => {
    if (!window.confirm(`Удалить Telegram ID ${telegramId} из белого списка?`)) return;
    try {
      setTelegramBusyId(telegramId);
      const resp = await api.deleteTelegramWhitelistItem(telegramId);
      if (resp.success) {
        onShowNotification?.('Telegram ID удалён', 'success');
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка удаления: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка удаления Telegram ID: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleRefreshTelegramId = async (telegramId: string) => {
    try {
      setTelegramBusyId(telegramId);
      const resp = await api.refreshTelegramWhitelistItem(telegramId);
      if (resp.success) {
        onShowNotification?.('Профиль обновлён', 'success');
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка обновления: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка обновления профиля: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleDeactivateTelegramChild = async (kingTelegramId: string, childTelegramId: string) => {
    try {
      setTelegramBusyId(kingTelegramId);
      const resp = await api.deactivateTelegramChild(kingTelegramId, childTelegramId);
      if (resp.success) {
        onShowNotification?.('Доступ ребёнка отключён', 'success');
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleActivateTelegramChild = async (kingTelegramId: string, childTelegramId: string) => {
    try {
      setTelegramBusyId(kingTelegramId);
      const resp = await api.activateTelegramChild(kingTelegramId, childTelegramId);
      if (resp.success) {
        onShowNotification?.('Доступ ребёнка включён', 'success');
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const openTransferModal = (fromTelegramId: string) => {
    setTransferFromId(fromTelegramId);
    setTransferToId('');
    setTransferOpen(true);
  };

  const closeTransferModal = () => {
    if (transferBusy) return;
    setTransferOpen(false);
  };

  const handleTransferTelegramId = async () => {
    const fromId = normalizeTelegramId(transferFromId);
    const toId = normalizeTelegramId(transferToId);
    if (!fromId || !toId) {
      onShowNotification?.('Введите корректный новый Telegram ID (только цифры)', 'error');
      return;
    }
    if (fromId === toId) {
      onShowNotification?.('Нельзя переносить на тот же Telegram ID', 'error');
      return;
    }
    if (!window.confirm(`Полностью перенести Telegram ID:\n${fromId} → ${toId}\n\nСтарый ID потеряет доступ, новый получит тот же профиль, ящики, дочки/мамку и шары.`)) return;

    try {
      setTransferBusy(true);
      const resp = await api.transferTelegramId(fromId, toId);
      if (resp.success) {
        const childLinks = Number((resp.data as any)?.movedChildLinksCount || 0) + Number((resp.data as any)?.movedKingLinksCount || 0);
        onShowNotification?.(`Перенос выполнен. Доступ переехал на новый Telegram ID${childLinks ? `, TG-связей перенесено: ${childLinks}` : ''}`, 'success');
        setTransferOpen(false);
        await reloadTelegramItems();
      } else {
        onShowNotification?.('Ошибка переноса: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка переноса: ' + (e as Error).message, 'error');
    } finally {
      setTransferBusy(false);
    }
  };

  const openShareModal = (targetTelegramId: string) => {
    setShareTargetId(targetTelegramId);
    setShareSourceId('');
    setShareOpen(true);
  };

  const closeShareModal = () => {
    if (shareBusy) return;
    setShareOpen(false);
  };

  const handleShareMailboxes = async () => {
    const targetId = normalizeTelegramId(shareTargetId);
    const sourceId = normalizeTelegramId(shareSourceId);
    if (!targetId || !sourceId) {
      onShowNotification?.('Введите корректный Telegram ID источника (только цифры)', 'error');
      return;
    }
    if (targetId === sourceId) {
      onShowNotification?.('Нельзя шарить ящики самому себе', 'error');
      return;
    }
    if (!window.confirm(`Расшарить все текущие ящики:\n${sourceId} → ${targetId} ?`)) return;

    try {
      setShareBusy(true);
      const resp = await api.shareMailboxesByTelegram(targetId, sourceId);
      if (resp.success) {
        const sharedNow = Number((resp.data as any)?.sharedNow || 0);
        const sourceTotal = Number((resp.data as any)?.sourceActiveMailboxes || 0);
        if (sharedNow > 0) {
          onShowNotification?.(`Шаринг выполнен. Добавлено ящиков: ${sharedNow} из ${sourceTotal}`, 'success');
        } else {
          onShowNotification?.('Новых ящиков для шаринга нет (всё уже расшарено)', 'success');
        }
        setShareOpen(false);
      } else {
        onShowNotification?.('Ошибка шаринга: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка шаринга: ' + (e as Error).message, 'error');
    } finally {
      setShareBusy(false);
    }
  };

  const reloadUserChildren = async (kingUserId: number) => {
    try {
      const resp = await api.getUserChildren(kingUserId);
      if (resp.success && resp.data) {
        setChildrenList(resp.data.children || []);
      } else {
        setChildrenList([]);
      }
    } catch {
      setChildrenList([]);
    }
  };

  const openChildrenModal = async (kingUserId: number) => {
    setChildrenKingUserId(kingUserId);
    setChildrenAddValue('');
    setChildrenOpen(true);
    await reloadUserChildren(kingUserId);
  };

  const closeChildrenModal = () => {
    if (childrenBusy) return;
    setChildrenOpen(false);
  };

  const handleAddChildUserId = async (childUserId: number) => {
    if (!childrenKingUserId) return;
    if (!Number.isFinite(childUserId) || childUserId <= 0) {
      onShowNotification?.('Введите корректный child userId', 'error');
      return;
    }
    try {
      setChildrenBusy(true);
      const resp = await api.linkUserChild(childrenKingUserId, childUserId);
      if (resp.success) {
        onShowNotification?.('Дочерний аккаунт добавлен', 'success');
        setChildrenAddValue('');
        await reloadUserChildren(childrenKingUserId);
      } else {
        onShowNotification?.('Ошибка добавления: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка добавления: ' + (e as Error).message, 'error');
    } finally {
      setChildrenBusy(false);
    }
  };

  const handleAddChildUser = async () => {
    if (!childrenKingUserId) return;
    const childUserId = parseInt(String(childrenAddValue || '').trim(), 10);
    await handleAddChildUserId(childUserId);
  };

  const handleRemoveChildUser = async (childUserId: number) => {
    if (!childrenKingUserId) return;
    if (!window.confirm(`Отвязать child userId=${childUserId} от king userId=${childrenKingUserId}?`)) return;
    try {
      setChildrenBusy(true);
      const resp = await api.unlinkUserChild(childrenKingUserId, childUserId);
      if (resp.success) {
        onShowNotification?.('Дочерний аккаунт отвязан', 'success');
        await reloadUserChildren(childrenKingUserId);
      } else {
        onShowNotification?.('Ошибка отвязки: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка отвязки: ' + (e as Error).message, 'error');
    } finally {
      setChildrenBusy(false);
    }
  };

  const loadTelegramChildren = async (kingTelegramId: string) => {
    try {
      const resp = await api.getTelegramChildren(kingTelegramId);
      if (resp.success && resp.data) {
        setTelegramChildrenByKing(prev => ({ ...prev, [kingTelegramId]: resp.data!.children || [] }));
      } else {
        setTelegramChildrenByKing(prev => ({ ...prev, [kingTelegramId]: [] }));
      }
    } catch {
      setTelegramChildrenByKing(prev => ({ ...prev, [kingTelegramId]: [] }));
    }
  };

  const toggleTelegramChildren = async (kingTelegramId: string) => {
    setTelegramChildrenOpenByKing(prev => {
      const next = { ...prev };
      if (next[kingTelegramId]) {
        delete next[kingTelegramId];
      } else {
        next[kingTelegramId] = true;
      }
      return next;
    });
    if (!telegramChildrenByKing[kingTelegramId]) {
      await loadTelegramChildren(kingTelegramId);
    }
  };

  const handleAddTelegramChild = async (kingTelegramId: string) => {
    const raw = String(telegramChildAddByKing[kingTelegramId] || '').trim();
    const childTelegramId = normalizeTelegramId(raw);
    if (!childTelegramId) {
      onShowNotification?.('Введите корректный Telegram ID дочки (только цифры)', 'error');
      return;
    }
    try {
      setTelegramBusyId(kingTelegramId);
      const resp = await api.linkTelegramChild(kingTelegramId, childTelegramId);
      if (resp.success) {
        onShowNotification?.('Telegram-дочка добавлена', 'success');
        setTelegramChildAddByKing(prev => ({ ...prev, [kingTelegramId]: '' }));
        await loadTelegramChildren(kingTelegramId);
      } else {
        onShowNotification?.('Ошибка добавления: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка добавления: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleRemoveTelegramChild = async (kingTelegramId: string, childTelegramId: string) => {
    if (!window.confirm(`Отвязать Telegram-дочку ${childTelegramId} от ${kingTelegramId}?`)) return;
    try {
      setTelegramBusyId(kingTelegramId);
      const resp = await api.unlinkTelegramChild(kingTelegramId, childTelegramId);
      if (resp.success) {
        onShowNotification?.('Telegram-дочка отвязана', 'success');
        await loadTelegramChildren(kingTelegramId);
      } else {
        onShowNotification?.('Ошибка отвязки: ' + (resp.message || 'неизвестная ошибка'), 'error');
      }
    } catch (e) {
      onShowNotification?.('Ошибка отвязки: ' + (e as Error).message, 'error');
    } finally {
      setTelegramBusyId(null);
    }
  };

  const handleCopyMailboxEmail = (fullEmail: string) => {
    const doCopy = async () => {
      try {
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(fullEmail);
        } else {
          const ta = document.createElement('textarea');
          ta.value = fullEmail;
          ta.setAttribute('readonly', 'true');
          ta.style.position = 'fixed';
          ta.style.top = '-1000px';
          ta.style.left = '-1000px';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(ta);
          if (!ok) throw new Error('copy_failed');
        }
        onShowNotification?.(`Email ${fullEmail} скопирован!`, 'success');
      } catch {
        onShowNotification?.('Ошибка при копировании', 'error');
      }
    };
    void doCopy();
  };

  const handleDeleteMailbox = async (mailboxId: string, mailboxName: string) => {
    const confirmed = window.confirm(
      `Вы действительно хотите удалить ящик ${mailboxName}?\n\n` +
      `Это действие необратимо и удалит:\n` +
      `• Сам ящик\n` +
      `• ВСЕ письма в этом ящике\n\n` +
      `Продолжить?`
    );
    
    if (confirmed) {
      try {
        const response = await api.deleteAdminMailbox(mailboxId);
        if (response.success) {
          const deletedCount = (response.data as any)?.deletedEmails || 0;
          onShowNotification?.(
            `Ящик ${mailboxName} удален. Удалено писем: ${deletedCount}`, 
            'success'
          );
          // Reload from server because list is paginated/sorted server-side.
          await reloadMailboxes();
        } else {
          onShowNotification?.('Ошибка удаления: ' + response.message, 'error');
        }
      } catch (error) {
        onShowNotification?.('Ошибка удаления ящика', 'error');
      }
    }
  };

  // Фильтрация данных
  const filteredUsers = users
    // Hide children from the main list: show them only inside their king row.
    .filter((u: any) => !u?.parentUserId)
    .filter(user =>
      user.username.toLowerCase().includes(userSearch.toLowerCase()) ||
      user.note.toLowerCase().includes(userSearch.toLowerCase()) ||
      (Array.isArray(user.children) && user.children.some(k => String(k.username || '').toLowerCase().includes(userSearch.toLowerCase())))
    );

  // Mailboxes are server-filtered/paginated.
  const filteredMailboxes = mailboxes;

  const getTelegramWhitelistDisplayName = (telegramId?: string | number | null) => {
    if (telegramId == null) return '';
    const id = String(telegramId);
    const item: any = telegramItems.find((it: any) => String(it?.telegramId) === id);
    if (!item) return '';
    return (
      item.displayName ||
      [item.firstName, item.lastName].filter(Boolean).join(' ') ||
      (item.username ? `@${item.username}` : '') ||
      ''
    );
  };

  const getTelegramProfileDisplayName = (
    tgProfile?: { username?: string | null; firstName?: string | null; lastName?: string | null } | null,
    fallback?: string
  ) => {
    const fullName = [tgProfile?.firstName, tgProfile?.lastName].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;
    if (tgProfile?.username) return `@${tgProfile.username}`;
    return fallback || '—';
  };

  const renderUsersTab = () => (
    <div className="admin-tab-content">
      <div className="admin-search">
        <input
          type="text"
          placeholder="Поиск по имени пользователя или заметке..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="admin-search-input"
        />
      </div>
      
      <div className="admin-content-area">
        <div className="admin-grid admin-users-grid">
          <div className="admin-grid-header">
            <div className="admin-grid-cell">Пользователь</div>
            <div className="admin-grid-cell">Заметка</div>
            <div className="admin-grid-cell">Пароль</div>
            <div className="admin-grid-cell admin-grid-cell-right">Действия</div>
          </div>

          {filteredUsers.map(user => (
            <div
              key={user.id}
              className={`admin-grid-row ${!user.isApproved ? 'pending' : ''}`}
            >
              <div className="admin-grid-cell admin-user-cell">
                <div className="admin-user-main">
                  <span className="username">
                    {((user as any)?.telegramId
                      ? getTelegramWhitelistDisplayName((user as any).telegramId) || user.username
                      : user.username)}
                  </span>
                  <code className="admin-user-id" title="userId">{user.id}</code>
                  <div className="user-badges">
                    <span className={`status-badge ${user.isApproved ? 'approved' : 'pending'}`}>
                      {user.isApproved ? 'Одобрен' : 'Ожидает'}
                    </span>
                    <span className={`role-badge ${user.isAdmin ? 'admin' : 'user'}`}>
                      {user.isAdmin ? 'Админ' : 'Пользователь'}
                    </span>
                  </div>
                </div>
                {user.email && <div className="admin-user-sub">{user.email}</div>}
                {(user as any)?.telegramId && (
                  <div className="admin-user-sub admin-user-sub-telegram">
                    <code>tg_{(user as any).telegramId}</code>
                  </div>
                )}
                {Array.isArray(user.children) && user.children.length > 0 && (
                  <div className="admin-user-children">
                    {user.children.map((kid) => (
                      <span key={kid.id} className="admin-user-child-chip" title={kid.username}>
                        <span className="admin-user-child-color" style={{ backgroundColor: kid.color || '#1E9BFF' }} />
                        <span className="admin-user-child-name">{kid.username}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="admin-grid-cell admin-note-cell">
                {user.note ? user.note : <span className="admin-muted">—</span>}
              </div>

              <div className="admin-grid-cell admin-password-cell">
                {editingUserId === user.id ? (
                  <div className="password-edit">
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="password-input"
                      placeholder="Новый пароль"
                    />
                    <button onClick={() => handleChangePassword(user.id)} className="save-btn" title="Сохранить">
                      <i className="fas fa-check"></i>
                    </button>
                    <button onClick={() => {setEditingUserId(null); setNewPassword('');}} className="cancel-btn" title="Отмена">
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ) : (
                  <div className="password-display">
                    <code>{user.plainPassword}</code>
                    <button 
                      onClick={() => {setEditingUserId(user.id); setNewPassword('');}}
                      className="edit-btn"
                      title="Изменить пароль"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  </div>
                )}
              </div>

              <div className="admin-grid-cell admin-grid-cell-right admin-actions-cell">
                {!user.isApproved && (
                  <button 
                    onClick={() => handleApproveUser(user.id)}
                    className="approve-btn"
                  >
                    <i className="fas fa-check-circle"></i> Одобрить
                  </button>
                )}
                <button
                  type="button"
                  className="admin-icon-btn"
                  onClick={() => void openChildrenModal(user.id)}
                  title="Дочерние аккаунты"
                  aria-label="Дочерние аккаунты"
                >
                  <i className="fas fa-user-group"></i>
                </button>
                <button 
                  onClick={() => handleDeleteUser(user.id)}
                  className="delete-btn"
                >
                  <i className="fas fa-trash"></i> Удалить
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderMailboxesTab = () => {
    const sortOptions: Array<{ id: 'created_at' | 'owner_username' | 'owner_telegram_id'; label: string }> = [
      { id: 'created_at', label: 'По дате создания' },
      { id: 'owner_username', label: 'По логину владельца' },
      { id: 'owner_telegram_id', label: 'По Telegram ID владельца' },
    ];
    const currentSortLabel = sortOptions.find(o => o.id === mailboxSortBy)?.label || 'Сортировка';
    const ownersSelectedCount = mailboxOwnerIds.length;
    const ownersLabel =
      ownersSelectedCount === 0
        ? 'Все владельцы'
        : `Владельцы: ${ownersSelectedCount}`;

    return (
      <div className="admin-tab-content">
        <div className="admin-search">
          <input
            type="text"
            placeholder="Поиск по адресу / заметке / владельцу..."
            value={mailboxSearch}
            onChange={(e) => {
              const next = e.target.value;
              setMailboxSearch(next);
              // Debounce server search to avoid request spam.
              if (mailboxSearchDebounceRef.current) {
                window.clearTimeout(mailboxSearchDebounceRef.current);
              }
              mailboxSearchDebounceRef.current = window.setTimeout(() => {
                void reloadMailboxes();
              }, 300);
            }}
            className="admin-search-input"
          />
        </div>
        
        <div className="admin-content-area">
          <div className="admin-mailboxes-toolbar">
            <div className="admin-mailboxes-sort">
              <span className="admin-sort-label">Сортировка:</span>
              <div className="admin-dropdown">
                <button
                  type="button"
                  className={`admin-dropdown-btn ${mailboxSortOpen ? 'open' : ''}`}
                  ref={mailboxSortBtnRef}
                  onClick={() => setMailboxSortOpen(v => !v)}
                >
                  <span className="admin-dropdown-label">{currentSortLabel}</span>
                  <span className={`admin-dropdown-caret ${mailboxSortOpen ? 'open' : ''}`}>
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </button>

                {mailboxSortOpen && (
                  <div
                    className="admin-dropdown-menu admin-dropdown-menu-fixed"
                    role="menu"
                    aria-label="Сортировка ящиков"
                    ref={mailboxSortMenuRef}
                    style={mailboxSortMenuPos ? {
                      top: mailboxSortMenuPos.top,
                      left: mailboxSortMenuPos.left,
                      minWidth: mailboxSortMenuPos.minWidth
                    } : undefined}
                  >
                    {sortOptions.map(opt => (
                      <button
                        key={opt.id}
                        type="button"
                        className={`admin-dropdown-item ${opt.id === mailboxSortBy ? 'active' : ''}`}
                        onClick={() => {
                          setMailboxSortBy(opt.id);
                          setMailboxSortOpen(false);
                        }}
                        role="menuitem"
                      >
                        <span className="admin-dropdown-item-text">{opt.label}</span>
                        {opt.id === mailboxSortBy && <i className="fas fa-check"></i>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="admin-secondary-btn"
                onClick={() => {
                  setMailboxSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
                }}
                title="Переключить направление сортировки"
              >
                <i className={`fas ${mailboxSortDir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
              </button>
            </div>

            <div className="admin-mailboxes-filter">
              <span className="admin-sort-label">Владельцы:</span>
              <div className="admin-dropdown">
                <button
                  type="button"
                  className={`admin-dropdown-btn ${mailboxOwnersOpen ? 'open' : ''}`}
                  ref={mailboxOwnersBtnRef}
                  onClick={() => setMailboxOwnersOpen(v => !v)}
                >
                  <span className="admin-dropdown-label">{ownersLabel}</span>
                  <span className={`admin-dropdown-caret ${mailboxOwnersOpen ? 'open' : ''}`}>
                    <i className="fas fa-chevron-down"></i>
                  </span>
                </button>

                {mailboxOwnersOpen && (
                  <div
                    className="admin-dropdown-menu admin-dropdown-menu-fixed"
                    role="menu"
                    aria-label="Фильтр владельцев"
                    ref={mailboxOwnersMenuRef}
                    style={mailboxOwnersMenuPos ? {
                      top: mailboxOwnersMenuPos.top,
                      left: mailboxOwnersMenuPos.left,
                      minWidth: mailboxOwnersMenuPos.minWidth
                    } : undefined}
                  >
                    <button
                      type="button"
                      className={`admin-dropdown-item ${mailboxOwnerIds.length === 0 ? 'active' : ''}`}
                      onClick={() => setMailboxOwnerIds([])}
                      role="menuitem"
                    >
                      <span className="admin-dropdown-item-text">Все владельцы</span>
                      {mailboxOwnerIds.length === 0 && <i className="fas fa-check"></i>}
                    </button>
                    <div className="admin-dropdown-sep" />
                    {mailboxOwners.map(o => {
                      const checked = mailboxOwnerIds.includes(o.id);
                      const tg = o.telegramProfile || null;
                      const tgName =
                        getTelegramProfileDisplayName(tg, '');
                      const secondary =
                        o.telegramId ? `tg_${o.telegramId}` : o.username;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          className={`admin-dropdown-item ${checked ? 'active' : ''}`}
                          onClick={() => {
                            setMailboxOwnerIds(prev => (
                              prev.includes(o.id) ? prev.filter(id => id !== o.id) : [...prev, o.id]
                            ));
                          }}
                          role="menuitem"
                        >
                          <span className="admin-dropdown-item-text">
                            <span className="admin-owner-option">
                              <span className="admin-owner-option-avatar">
                                {tg?.photoUrl && !brokenOwnerAvatars[String(o.id)] ? (
                                  <img
                                    src={tg.photoUrl}
                                    alt=""
                                    onError={() => setBrokenOwnerAvatars(prev => (prev[String(o.id)] ? prev : { ...prev, [String(o.id)]: true }))}
                                  />
                                ) : (
                                  <i className="fas fa-user"></i>
                                )}
                              </span>
                              <span className="admin-owner-option-text">
                                <span className="admin-owner-option-title">
                                  {tgName || o.username}
                                </span>
                                <span className="admin-owner-option-sub">
                                  {secondary}{o.mailboxCount ? ` • ${o.mailboxCount}` : ''}
                                </span>
                              </span>
                            </span>
                          </span>
                          <span className="admin-dropdown-item-check">
                            <i className={`fas ${checked ? 'fa-check-square' : 'fa-square'}`}></i>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="admin-grid admin-mailboxes-grid">
            <div className="admin-grid-header">
              <div className="admin-grid-cell">Email</div>
              <div className="admin-grid-cell admin-grid-cell-center">Писем</div>
              <div className="admin-grid-cell admin-grid-cell-center">Владелец</div>
              <div className="admin-grid-cell admin-grid-cell-center">Заметка</div>
              <div className="admin-grid-cell admin-grid-cell-center">Пароль</div>
              <div className="admin-grid-cell admin-grid-cell-center">Создан</div>
              <div className="admin-grid-cell admin-grid-cell-center">Проверен</div>
              <div className="admin-grid-cell admin-grid-cell-center">Действия</div>
            </div>

            {filteredMailboxes.map(mailbox => (
              <div key={mailbox.id} className="admin-grid-row">
                <div className="admin-grid-cell admin-mailbox-email-cell">
                  <code className="admin-mailbox-email">{mailboxAddress(mailbox)}</code>
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => handleCopyMailboxEmail(mailboxAddress(mailbox))}
                    title="Скопировать"
                    aria-label="Скопировать email"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>

                <div className="admin-grid-cell admin-grid-cell-center">
                  <span className="admin-count-pill">{mailbox.emailCount}</span>
                </div>

                <div className="admin-grid-cell admin-grid-cell-center">
                  <div className="admin-owner-cell">
                    <span className="admin-owner-username">
                      {getTelegramProfileDisplayName(
                        mailbox.owner?.telegramProfile || null,
                        mailbox.owner?.username || '—'
                      )}
                    </span>
                    <span className="admin-owner-tg">
                      {mailbox.owner?.telegramId ? `tg_${mailbox.owner.telegramId}` : '—'}
                    </span>
                  </div>
                </div>

                <div className="admin-grid-cell admin-grid-cell-center admin-note-cell" title={mailbox.account || ''}>
                  {mailbox.account || '—'}
                </div>

                <div className="admin-grid-cell admin-grid-cell-center">
                  {mailbox.hasPassword ? (
                    <code className="mailbox-password">{mailbox.plainPassword}</code>
                  ) : (
                    <span className="no-password">Без пароля</span>
                  )}
                </div>

                <div className="admin-grid-cell admin-grid-cell-center admin-date-cell">
                  {mailbox.createdAt ? formatDateMsk(mailbox.createdAt) : '—'}
                </div>

                <div className="admin-grid-cell admin-grid-cell-center admin-date-cell">
                  {mailbox.lastChecked ? formatDateTimeMsk(mailbox.lastChecked) : 'Никогда'}
                </div>

                <div className="admin-grid-cell admin-grid-cell-center admin-actions-cell admin-actions-cell-center">
                  <button
                    type="button"
                    className="admin-icon-btn danger"
                    onClick={() => handleDeleteMailbox(mailbox.id, mailboxAddress(mailbox))}
                    title="Удалить ящик и все письма"
                    aria-label="Удалить ящик"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="admin-load-more">
            <button
              type="button"
              className="admin-secondary-btn"
              onClick={() => void loadMoreMailboxes()}
              disabled={!mailboxHasMore || mailboxLoading}
            >
              {mailboxLoading ? 'Загрузка...' : mailboxHasMore ? 'Загрузить ещё' : 'Больше нет'}
            </button>
          </div>
      </div>
    </div>
    );
  };

  const renderTelegramTab = () => {
    return (
      <div className="admin-tab-content">
        <div className="admin-content-area">
          <div className="admin-card">
            <div className="admin-card-header">
              <div className="admin-card-title">
                <i className="fab fa-telegram"></i>
                Белый список Telegram ID
              </div>
              <div className="admin-card-subtitle">
                Только эти Telegram ID смогут входить через Telegram.
              </div>
            </div>

            <div className="admin-card-body">
              <p className="admin-help-text">
                Каждый Telegram ID — отдельный элемент. Профиль подтягивается только если пользователь нажал <code>/start</code> в боте.
              </p>

              <div className="admin-telegram-add">
                <input
                  className="admin-input"
                  value={telegramAddValue}
                  onChange={(e) => setTelegramAddValue(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="Введите Telegram ID (только цифры)"
                  inputMode="numeric"
                  autoComplete="off"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleAddTelegramId();
                  }}
                />
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={handleAddTelegramId}
                  disabled={!!telegramBusyId}
                >
                  <i className="fas fa-plus"></i>
                  Добавить
                </button>
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={() => void reloadTelegramItems()}
                  disabled={!!telegramBusyId}
                  title="Перезагрузить список"
                >
                  <i className="fas fa-rotate"></i>
                </button>
              </div>

              <div className="admin-telegram-list">
                {telegramItems.length === 0 ? (
                  <div className="admin-empty">Список пуст</div>
                ) : (
                  telegramItems
                    .filter((it: any) => !it?.isChild)
                    .map((item: any) => {
                    const busy = telegramBusyId === item.telegramId;
                    const name =
                      item.displayName ||
                      [item.firstName, item.lastName].filter(Boolean).join(' ') ||
                      item.username ||
                      item.telegramId ||
                      '—';
                    const updated =
                      item.updatedAt ? formatDateTimeMsk(item.updatedAt) : null;
                    const kidsOpen = Boolean(telegramChildrenOpenByKing[item.telegramId]);
                    const kids = telegramChildrenByKing[item.telegramId] || [];
                    const kidAddValue = telegramChildAddByKing[item.telegramId] || '';

                    return (
                      <div
                        key={item.telegramId}
                        className={`admin-telegram-item ${item.isActive ? 'active' : 'inactive'}`}
                      >
                        <div className="admin-telegram-avatar">
                          {item.photoUrl && !brokenTelegramAvatars[item.telegramId] ? (
                            <img
                              src={item.photoUrl}
                              alt=""
                              onError={() => handleTelegramAvatarError(item.telegramId)}
                            />
                          ) : (
                            <i className="fab fa-telegram"></i>
                          )}
                        </div>

                        <div className="admin-telegram-meta">
                          <div className="admin-telegram-row">
                            <code className="admin-telegram-id">{item.telegramId}</code>
                            <span className={`admin-telegram-status ${item.isActive ? 'ok' : 'muted'}`}>
                              {item.isActive ? 'active' : 'inactive'}
                            </span>
                          </div>
                          <div className="admin-telegram-row subtle">
                            <span className="admin-telegram-name">{name}</span>
                            {updated && <span className="admin-telegram-updated">обновлено: {updated}</span>}
                          </div>
                          {item.lastError && (
                            <div className="admin-telegram-error" title={item.lastError}>
                              {item.lastError}
                            </div>
                          )}

                          {Array.isArray(item.children) && item.children.length > 0 && (
                            <div className="admin-telegram-children">
                              <div className="admin-telegram-children-list">
                                {item.children.map((kid: any) => {
                                  const kidTg = kid?.telegramId ? String(kid.telegramId) : null;
                                  if (!kidTg) return null;
                                  const kidTitle =
                                    kid?.displayName ||
                                    [kid?.firstName, kid?.lastName].filter(Boolean).join(' ') ||
                                    kid?.username ||
                                    kidTg;
                                  const color = kid?.color || '#1E9BFF';
                                  return (
                                    <div key={kidTg} className="admin-child-chip">
                                      <span className="admin-child-color" style={{ backgroundColor: color }} />
                                      <span className="admin-owner-option-avatar">
                                        {kid?.photoUrl && !brokenTelegramAvatars[kidTg] ? (
                                          <img
                                            src={kid.photoUrl}
                                            alt=""
                                            onError={() => handleTelegramAvatarError(kidTg)}
                                          />
                                        ) : (
                                          <i className="fas fa-user"></i>
                                        )}
                                      </span>
                                      <span className="admin-child-text" title={kidTitle}>
                                        {kidTitle}
                                      </span>
                                      <code className="admin-child-id">{kidTg}</code>
                                      {kid?.isActive === false && (
                                        <span className="admin-telegram-status muted" title="Доступ отключён">inactive</span>
                                      )}
                                      {kid?.lastError && (
                                        <span className="admin-telegram-status muted" title={kid.lastError}>profile?</span>
                                      )}
                                      <button
                                        type="button"
                                        className="admin-icon-btn"
                                        onClick={() => void handleRefreshTelegramId(kidTg)}
                                        disabled={busy}
                                        title="Обновить профиль ребёнка"
                                        aria-label="Обновить профиль ребёнка"
                                      >
                                        <i className="fas fa-rotate"></i>
                                      </button>
                                      {(kid?.isActive ?? true) ? (
                                        <button
                                          type="button"
                                          className="admin-icon-btn danger"
                                          onClick={() => void handleDeactivateTelegramChild(item.telegramId, kidTg)}
                                          disabled={busy}
                                          title="Отключить доступ ребёнку"
                                          aria-label="Отключить доступ ребёнку"
                                        >
                                          <i className="fas fa-ban"></i>
                                        </button>
                                      ) : (
                                        <button
                                          type="button"
                                          className="admin-icon-btn"
                                          onClick={() => void handleActivateTelegramChild(item.telegramId, kidTg)}
                                          disabled={busy}
                                          title="Включить доступ ребёнку"
                                          aria-label="Включить доступ ребёнку"
                                        >
                                          <i className="fas fa-play"></i>
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="admin-icon-btn"
                                        onClick={() => openShareModal(kidTg)}
                                        disabled={busy}
                                        title="Шаринг всех ящиков в этот профиль"
                                        aria-label="Шаринг всех ящиков в этот профиль"
                                      >
                                        <i className="fas fa-share-alt"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-icon-btn"
                                        onClick={() => openTransferModal(kidTg)}
                                        disabled={busy}
                                        title="Перенести Telegram ID ребёнка"
                                        aria-label="Перенести Telegram ID ребёнка"
                                      >
                                        <i className="fas fa-right-left"></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="admin-icon-btn danger"
                                        onClick={() => void handleRemoveTelegramChild(item.telegramId, kidTg)}
                                        disabled={busy}
                                        title="Отвязать ребёнка от king"
                                        aria-label="Отвязать ребёнка от king"
                                      >
                                        <i className="fas fa-xmark"></i>
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {kidsOpen && (
                            <div className="admin-telegram-children">
                              <div className="admin-telegram-children-add">
                                <input
                                  className="admin-input admin-telegram-child-input"
                                  value={kidAddValue}
                                  onChange={(e) => {
                                    const v = e.target.value.replace(/[^\d]/g, '');
                                    setTelegramChildAddByKing(prev => ({ ...prev, [item.telegramId]: v }));
                                  }}
                                  placeholder="Telegram ID дочки"
                                  inputMode="numeric"
                                  autoComplete="off"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') void handleAddTelegramChild(item.telegramId);
                                  }}
                                  disabled={busy}
                                />
                                <button
                                  type="button"
                                  className="admin-secondary-btn"
                                  onClick={() => void handleAddTelegramChild(item.telegramId)}
                                  disabled={busy}
                                  title="Добавить дочку"
                                >
                                  <i className="fas fa-plus"></i>
                                </button>
                                <button
                                  type="button"
                                  className="admin-secondary-btn"
                                  onClick={() => void loadTelegramChildren(item.telegramId)}
                                  disabled={busy}
                                  title="Обновить список дочек"
                                >
                                  <i className="fas fa-rotate"></i>
                                </button>
                              </div>

                              <div className="admin-telegram-children-list">
                                {kids.length === 0 ? (
                                  <div className="admin-muted">Дочек нет</div>
                                ) : (
                                  kids.map((kid: any) => {
                                    const kidTg = kid?.telegramId ? String(kid.telegramId) : null;
                                    if (!kidTg) return null;
                                    const kidProfile = kid?.telegramProfile || null;
                                    const kidTitle =
                                      (kidProfile?.username ? `@${kidProfile.username}` : '') ||
                                      [kidProfile?.firstName, kidProfile?.lastName].filter(Boolean).join(' ') ||
                                      kidTg;
                                    const color = kid?.color || '#1E9BFF';
                                    return (
                                      <div key={kidTg} className="admin-child-chip">
                                        <span className="admin-child-color" style={{ backgroundColor: color }} />
                                        <span className="admin-owner-option-avatar">
                                          {kid?.photoUrl && !brokenTelegramAvatars[kidTg] ? (
                                            <img
                                              src={kid.photoUrl}
                                              alt=""
                                              onError={() => handleTelegramAvatarError(kidTg)}
                                            />
                                          ) : (
                                            <i className="fas fa-user"></i>
                                          )}
                                        </span>
                                        <span className="admin-child-text" title={kidTitle}>
                                          {kidTitle}
                                        </span>
                                        <code className="admin-child-id">{kidTg}</code>
                                        {kid?.lastError && (
                                          <span className="admin-telegram-status muted" title={kid.lastError}>profile?</span>
                                        )}
                                        <button
                                          type="button"
                                          className="admin-icon-btn"
                                          onClick={() => openTransferModal(kidTg)}
                                          disabled={busy}
                                          title="Перенести Telegram ID ребёнка"
                                          aria-label="Перенести Telegram ID ребёнка"
                                        >
                                          <i className="fas fa-right-left"></i>
                                        </button>
                                        <button
                                          type="button"
                                          className="admin-icon-btn danger"
                                          onClick={() => void handleRemoveTelegramChild(item.telegramId, kidTg)}
                                          disabled={busy}
                                          title="Отвязать"
                                          aria-label="Отвязать"
                                        >
                                          <i className="fas fa-xmark"></i>
                                        </button>
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="admin-telegram-actions">
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => void toggleTelegramChildren(item.telegramId)}
                            disabled={busy}
                            title={kidsOpen ? 'Скрыть дочек' : 'Дочерние аккаунты'}
                            aria-label="Дочерние аккаунты"
                          >
                            <i className="fas fa-user-plus"></i>
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => void handleRefreshTelegramId(item.telegramId)}
                            disabled={busy}
                            title="Обновить профиль"
                            aria-label="Обновить профиль"
                          >
                            <i className="fas fa-rotate"></i>
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => openTransferModal(item.telegramId)}
                            disabled={busy}
                            title="Перенос Telegram ID"
                            aria-label="Перенос Telegram ID"
                          >
                            <i className="fas fa-right-left"></i>
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn"
                            onClick={() => openShareModal(item.telegramId)}
                            disabled={busy}
                            title="Шаринг всех ящиков в этот профиль"
                            aria-label="Шаринг всех ящиков в этот профиль"
                          >
                            <i className="fas fa-share-alt"></i>
                          </button>
                          <button
                            type="button"
                            className="admin-icon-btn danger"
                            onClick={() => void handleDeleteTelegramId(item.telegramId)}
                            disabled={busy}
                            title="Удалить из whitelist"
                            aria-label="Удалить из whitelist"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>

        {childrenOpen && (
          <div className="admin-modal-overlay" onMouseDown={closeChildrenModal}>
            <div
              className="admin-modal"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Дочерние аккаунты"
            >
              <div className="admin-modal-header">
                <div className="admin-modal-title">Дочерние аккаунты</div>
                <button
                  type="button"
                  className="admin-icon-btn"
                  onClick={closeChildrenModal}
                  disabled={childrenBusy}
                  aria-label="Закрыть"
                  title="Закрыть"
                >
                  <i className="fas fa-xmark"></i>
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="admin-help-text">
                  King userId: <code>{childrenKingUserId ?? '—'}</code>
                </div>

                <div className="admin-telegram-children-add admin-children-add">
                  <input
                    className="admin-input"
                    value={childrenAddValue}
                    onChange={(e) => setChildrenAddValue(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder="child userId"
                    inputMode="numeric"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleAddChildUser();
                    }}
                    disabled={childrenBusy}
                  />
                  <button
                    type="button"
                    className="admin-secondary-btn"
                    onClick={() => void handleAddChildUser()}
                    disabled={childrenBusy}
                    title="Добавить дочку"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                  {childrenKingUserId && (
                    <button
                      type="button"
                      className="admin-secondary-btn"
                      onClick={() => void reloadUserChildren(childrenKingUserId)}
                      disabled={childrenBusy}
                      title="Обновить список"
                    >
                      <i className="fas fa-rotate"></i>
                    </button>
                  )}
                </div>

                <div className="admin-telegram-children-list admin-children-list">
                  {childrenList.length === 0 ? (
                    <div className="admin-muted">Дочек нет</div>
                  ) : (
                    childrenList.map((kid) => (
                      <div key={kid.id} className="admin-child-chip">
                        <span className="admin-child-color" style={{ backgroundColor: kid.color || '#1E9BFF' }} />
                        <span className="admin-child-text" title={kid.username}>
                          {kid.username}
                        </span>
                        <code className="admin-child-id">{kid.id}</code>
                        {kid.telegramId && <code className="admin-child-id">tg_{kid.telegramId}</code>}
                        <button
                          type="button"
                          className="admin-icon-btn danger"
                          onClick={() => void handleRemoveChildUser(kid.id)}
                          disabled={childrenBusy}
                          title="Отвязать"
                          aria-label="Отвязать"
                        >
                          <i className="fas fa-xmark"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="admin-help-text">
                  Перенос существующих пользователей в «дочки» (выбери и нажми +):
                </div>
                <div className="admin-children-picker">
                  {users
                    .filter(u => u.id !== (childrenKingUserId || -1))
                    .filter(u => !(u as any)?.parentUserId)
                    .filter(u => !childrenList.some(k => k.id === u.id))
                    .filter(u => {
                      const q = String(childrenAddValue || '').toLowerCase().trim();
                      if (!q) return true;
                      return (
                        u.username.toLowerCase().includes(q) ||
                        String(u.id).includes(q) ||
                        String((u as any)?.telegramId || '').includes(q)
                      );
                    })
                    .slice(0, 10)
                    .map(u => (
                      <div key={u.id} className="admin-child-chip admin-child-picker-row">
                        <span className="admin-child-text" title={u.username}>{u.username}</span>
                        <code className="admin-child-id">{u.id}</code>
                        {(u as any)?.telegramId && <code className="admin-child-id">tg_{(u as any).telegramId}</code>}
                        <button
                          type="button"
                          className="admin-icon-btn"
                          onClick={() => { void handleAddChildUserId(u.id); }}
                          disabled={childrenBusy}
                          title="Сделать дочкой"
                          aria-label="Сделать дочкой"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {transferOpen && (
          <div className="admin-modal-overlay" onMouseDown={closeTransferModal}>
            <div
              className="admin-modal"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Перенос Telegram ID"
            >
              <div className="admin-modal-header">
                <div className="admin-modal-title">Перенос Telegram ID</div>
                <button
                  type="button"
                  className="admin-icon-btn"
                  onClick={closeTransferModal}
                  disabled={transferBusy}
                  aria-label="Закрыть"
                  title="Закрыть"
                >
                  <i className="fas fa-xmark"></i>
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="admin-field">
                  <div className="admin-field-label">Старый ID</div>
                  <input className="admin-input" value={transferFromId} readOnly />
                </div>
                <div className="admin-field">
                  <div className="admin-field-label">Новый ID</div>
                  <input
                    className="admin-input"
                    value={transferToId}
                    onChange={(e) => setTransferToId(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder="Введите новый Telegram ID (только цифры)"
                    inputMode="numeric"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleTransferTelegramId();
                    }}
                    disabled={transferBusy}
                  />
                </div>
                <div className="admin-help-text">
                  После переноса старый профиль сохраняет свой userId, роли, ящики, шары и связи, но логин переезжает на новый <code>telegram_id</code>. Старый ID будет отключён.
                </div>
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={closeTransferModal}
                  disabled={transferBusy}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={() => void handleTransferTelegramId()}
                  disabled={transferBusy}
                >
                  <i className="fas fa-arrow-right-arrow-left"></i>
                  Перенести
                </button>
              </div>
            </div>
          </div>
        )}

        {shareOpen && (
          <div className="admin-modal-overlay" onMouseDown={closeShareModal}>
            <div
              className="admin-modal"
              onMouseDown={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Шаринг ящиков"
            >
              <div className="admin-modal-header">
                <div className="admin-modal-title">Шаринг всех ящиков</div>
                <button
                  type="button"
                  className="admin-icon-btn"
                  onClick={closeShareModal}
                  disabled={shareBusy}
                  aria-label="Закрыть"
                  title="Закрыть"
                >
                  <i className="fas fa-xmark"></i>
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="admin-field">
                  <div className="admin-field-label">Кому шарим (выбранный профиль)</div>
                  <input className="admin-input" value={shareTargetId} readOnly />
                </div>
                <div className="admin-field">
                  <div className="admin-field-label">С какого Telegram ID шарим</div>
                  <input
                    className="admin-input"
                    value={shareSourceId}
                    onChange={(e) => setShareSourceId(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder="Введите Telegram ID источника (только цифры)"
                    inputMode="numeric"
                    autoComplete="off"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void handleShareMailboxes();
                    }}
                    disabled={shareBusy}
                  />
                </div>
                <div className="admin-help-text">
                  Шаринг делает снимок ящиков на текущий момент. При повторном шаринге из того же источника добавятся только новые ящики.
                </div>
              </div>

              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-secondary-btn"
                  onClick={closeShareModal}
                  disabled={shareBusy}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="admin-primary-btn"
                  onClick={() => void handleShareMailboxes()}
                  disabled={shareBusy}
                >
                  <i className="fas fa-share-alt"></i>
                  Расшарить
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-panel">      
      <div className="admin-scope-bar">
        <div className="admin-scope-switcher">
          <button
            type="button"
            className={`admin-scope-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <i className="fas fa-users"></i>
            Пользователи
          </button>
          <button
            type="button"
            className={`admin-scope-btn ${activeTab === 'mailboxes' ? 'active' : ''}`}
            onClick={() => setActiveTab('mailboxes')}
          >
            <i className="fas fa-inbox"></i>
            Ящики
          </button>
          <button
            type="button"
            className={`admin-scope-btn ${activeTab === 'telegram' ? 'active' : ''}`}
            onClick={() => setActiveTab('telegram')}
          >
            <i className="fab fa-telegram"></i>
            Telegram
          </button>
          <div className={`admin-scope-indicator ${activeTab}`}></div>
        </div>
      </div>

      <div className="admin-content">
        {activeTab === 'users' && renderUsersTab()}
        {activeTab === 'mailboxes' && renderMailboxesTab()}
        {activeTab === 'telegram' && renderTelegramTab()}
      </div>
    </div>
  );
};

export default AdminPanel;
